import { Loader2, CheckCircle, Shield } from "lucide-react";
import { useAuth } from "context/AuthContext";

interface AuthSpinnerProps {
  message?: string;
  isSuccess?: boolean;
  step?: "authenticating" | "verifying" | "connecting" | "success";
}

function AuthSpinner({
  message = "Authenticating...",
  isSuccess = false,
  step = "authenticating",
}: AuthSpinnerProps) {
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const getStepMessage = () => {
    switch (step) {
      case "authenticating":
        return locale === "es"
          ? "Autenticando con Google..."
          : "Authenticating with Google...";
      case "verifying":
        return locale === "es"
          ? "Verificando tu cuenta..."
          : "Verifying your account...";
      case "connecting":
        return locale === "es"
          ? "Conectando con el servidor..."
          : "Connecting to server...";
      case "success":
        return locale === "es"
          ? "¡Éxito! Redirigiendo..."
          : "Success! Redirecting...";
      default:
        return locale === "es" ? "Autenticando..." : message;
    }
  };

  const getStepIcon = () => {
    if (isSuccess) {
      return (
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center animate-pulse">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
      );
    }

    return (
      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl p-8 flex flex-col items-center max-w-sm w-full mx-4 border border-gray-100">
        <div className="relative mb-6">{getStepIcon()}</div>

        <h2 className="text-xl font-semibold text-gray-800 mb-2 text-center">
          {isSuccess
            ? locale === "es"
              ? "¡Bienvenido a Eloria!"
              : "Welcome to Eloria!"
            : locale === "es"
              ? "Por favor espera"
              : "Please wait"}
        </h2>

        <p className="text-gray-600 text-center text-sm mb-4">
          {getStepMessage()}
        </p>

        {!isSuccess && (
          <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
              style={{
                width:
                  step === "authenticating"
                    ? "25%"
                    : step === "verifying"
                    ? "50%"
                    : step === "connecting"
                    ? "75%"
                    : "100%",
              }}
            />
          </div>
        )}

        {isSuccess && (
          <div className="flex items-center gap-2 text-green-600 text-sm mt-2">
            <Shield className="w-4 h-4" />
            <span>
              {locale === "es"
                ? "Autenticación segura completada"
                : "Secure authentication completed"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default AuthSpinner;
